/**
 * Inline status / count chip. Used by Tabs (tab badges), Navbar
 * (alarm count when integrated with the bell drawer), inventory
 * rows (state pills), and the chassis card grid.
 */
import type { ReactNode } from "react";

export type BadgeTone =
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "muted";

const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: "border-slate-700 bg-slate-800 text-slate-300",
  info: "border-sky-700 bg-sky-900/40 text-sky-200",
  success: "border-emerald-700 bg-emerald-900/40 text-emerald-200",
  warning: "border-amber-700 bg-amber-900/40 text-amber-200",
  danger: "border-red-700 bg-red-900/40 text-red-200",
  muted: "border-slate-800 bg-slate-900 text-slate-500",
};

export function Badge({
  tone = "neutral",
  children,
  className,
  title,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={[
        // Keep the tone-coloured border (it carries the badge's semantics,
        // not depth). tabular-nums aligns count badges like "Pending (5)".
        "inline-flex items-center rounded border px-1.5 py-0.5",
        "text-[10px] uppercase tracking-wide tabular-nums",
        TONE_CLASSES[tone],
        className ?? "",
      ].join(" ")}
    >
      {children}
    </span>
  );
}
